/**
 * Screener-response status groupings shared by core-api and the frontends.
 *
 * The `ScreenerResponseStatus` **enum** deliberately stays declared in
 * `services/core-api/src/v4/features/screener-responses/enums/`: codegen
 * (`scripts/generate-pure-database-types.ts`) collects enum *declarations* under
 * that folder to emit `common/database-entities`' `pure-entities.ts`, so moving
 * or re-exporting it there drops it from the generated output. These groupings
 * are therefore expressed as string-literal unions — the same shape the
 * frontends' generated `types.gen.ts` uses — and core-api's enum members are
 * assignable to them, so both sides stay cast-free.
 *
 * **Two vocabularies, do not conflate them.** `REJECTED_LIFECYCLE_STATUSES`
 * groups rows for the researcher's Rejected *view* and treats only
 * REJECTEDQUALITY and INVALIDRESPONSE as rejected, whereas
 * `SLOT_RELEASING_STATUSES` is recruitment-slot accounting and also treats
 * CANCELLED and NOSHOW as terminal. They answer different questions; reaching
 * for the wrong one is a silent bug.
 *
 * Each grouping is exported three ways: a readonly **array** (for Mongo
 * `$in`/`$nin`), a union **type** derived from it via `(typeof x)[number]`, and a
 * **type predicate** (for membership tests, which narrow so the branch body
 * keeps the precise type). Deriving the type from the array means the values are
 * declared exactly once and cannot drift.
 */

/**
 * Statuses in which the participant is no longer a candidate for this project:
 * they stop filling a recruitment slot (so the invite caps can invite a
 * replacement) and stop consuming sourcing headroom (so recruiting can find
 * one).
 *
 * **Classify every new `ScreenerResponseStatus` here.** This list drives the
 * visible-qualified buffer's exclusion, and its complement is what the invite
 * caps enumerate positively; an unclassified status silently keeps occupying
 * recruitment capacity. core-api's `recruitment-caps.constants.spec.ts` fails if
 * a new enum member is not classified.
 *
 * These are also exactly the statuses core-api's `invite()` accepts as
 * re-invite sources (its `allowedStatuses`, minus PENDING/INVITED), so the
 * researcher UI keys its "Re-invite" affordance off this same list.
 */
export const SLOT_RELEASING_STATUSES = [
  'CANCELLED',
  'REJECTEDQUALITY',
  'INVALIDRESPONSE',
  'NOSHOW',
] as const;

export type SlotReleasingStatus = (typeof SLOT_RELEASING_STATUSES)[number];

/**
 * Lifecycle statuses that read as "Rejected" to the researcher, alongside
 * manual "Not a fit" rejections (which carry a `disqualification` record and
 * stay on PENDING — they are not a status at all).
 */
export const REJECTED_LIFECYCLE_STATUSES = [
  'REJECTEDQUALITY',
  'INVALIDRESPONSE',
] as const;

export type RejectedLifecycleStatus =
  (typeof REJECTED_LIFECYCLE_STATUSES)[number];

const SLOT_RELEASING_STATUS_SET: ReadonlySet<string> = new Set(
  SLOT_RELEASING_STATUSES
);

const REJECTED_LIFECYCLE_STATUS_SET: ReadonlySet<string> = new Set(
  REJECTED_LIFECYCLE_STATUSES
);

/**
 * Is this participant out of the running for the project (and therefore
 * re-invitable)? Takes a plain `string` so both an enum member and a generated
 * string-literal union are accepted without a cast, and narrows to
 * `SlotReleasingStatus` so the branch body keeps the precise type.
 */
export function isSlotReleasingStatus(
  status: string | undefined
): status is SlotReleasingStatus {
  return status !== undefined && SLOT_RELEASING_STATUS_SET.has(status);
}

/**
 * Does this status read as "Rejected" to the researcher? Note this is *not* the
 * full Rejected view: manual "Not a fit" rows stay on PENDING and carry a
 * `disqualification` record instead, so callers rendering that view must check
 * both.
 */
export function isRejectedLifecycleStatus(
  status: string | undefined
): status is RejectedLifecycleStatus {
  return status !== undefined && REJECTED_LIFECYCLE_STATUS_SET.has(status);
}
